import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button, Popconfirm, Tooltip } from "antd";
import { CalendarOutlined, SendOutlined } from "@ant-design/icons";
import Layout from '../../../components/Layout';
import { post } from '../../../services/ApiService';
import "../../custom-style.css";
import Loader from "../../../components/Loader";
import Pagination from '../../../components/Pagination';
import Breadcrumbs from "../../../components/Breadcrumbs";
import DataTable from "../SignPost/DataTable";
import DispatchDateModal, { saveDispatchDate } from "../Assessment/components/DispatchDateModal";
import { DateFormater, formatDaysUntilDispatch } from "../../../services/Helper";

const DEFAULT_FILTERS = {
    orderId: '',
    customer: '',
    limit: '20',
    sortOrder: 'soonest',
};

const scheduledDispatchColumns = [
    {
        key: 'scheduledDispatchDate',
        header: 'Dispatch On',
        className: 'order-panel-nowrap',
        render: (order) => (
            <span style={{ fontSize: '12px' }}>
                {DateFormater(order.scheduledDispatchDate)}
                <br />
                <span className="text-muted">in {formatDaysUntilDispatch(order)}</span>
            </span>
        ),
    },
];

const ScheduledDispatchOrders = () => {

    const [isLoading, setIsLoading] = useState(true);
    const [isNotFound, setNotFound] = useState(false);
    const [orders, setOrders] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [rescheduleOrder, setRescheduleOrder] = useState(null);

    const fetchData = useCallback(async () => {
        try {
            setIsLoading(true);
            const res = await post('api/orders/scheduled-dispatch/list', { page: currentPage, ...filters });
            if (res.success) {
                setOrders(res.orders);
                setTotalPages(res.totalPages);
                setNotFound(false);
            } else {
                setOrders([]);
                setTotalPages(1);
                setNotFound(true);
            }
        } catch (error) {
            setOrders([]);
            setTotalPages(1);
            setNotFound(true);
            console.error(error.message);
        } finally {
            setIsLoading(false);
        }
    }, [currentPage, filters]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleFilterChange = (e) => {
        setCurrentPage(1);
        setFilters({
            ...filters,
            [e.target.name]: e.target.value
        });
    };

    const resetFilters = () => {
        setFilters(DEFAULT_FILTERS);
        setCurrentPage(1);
    };

    const handleDispatchDateSaved = () => {
        setRescheduleOrder(null);
        fetchData();
    };

    const dispatchNow = async (order) => {
        if (await saveDispatchDate(order._id, null)) fetchData();
    };

    const renderActions = (order) => (
        <div className="order-panel-icon-actions">
            <Tooltip title="Reschedule">
                <Button
                    icon={<CalendarOutlined />}
                    aria-label="Reschedule"
                    onClick={() => setRescheduleOrder(order)}
                />
            </Tooltip>
            <Popconfirm
                title="Dispatch now?"
                description="The dispatch date will be cleared and the order goes with the next courier run."
                okText="Dispatch Now"
                cancelText="Cancel"
                onConfirm={() => dispatchNow(order)}
            >
                <Tooltip title="Dispatch now">
                    <Button className="order-panel-primary-btn" icon={<SendOutlined />} aria-label="Dispatch now" />
                </Tooltip>
            </Popconfirm>
        </div>
    );

  return (
    <>
        <Layout>
            <div className="row mb-9 align-items-center justify-content-between">
                <div className="col-sm-6 mb-8 mb-sm-0">
                <h2 className="fs-4 mb-0">Scheduled Dispatch</h2>
                <Breadcrumbs />
                </div>
            </div>
            <div className="card mb-4 rounded-4 p-7">
                <div className="card-header bg-transparent px-0 pt-0 pb-7">
                <div className="row align-items-center justify-content-between mb-4">
                    <div className="col-lg-3 col-12 col-sm-12 col-md-6 mr-auto mb-md-0 mb-6">
                        <input type="text" placeholder="Search by order id..." name="orderId" value={filters.orderId} onChange={handleFilterChange} className="form-control bg-input border-0" />
                    </div>
                    <div className="col-lg-3 col-12 col-md-6 col-sm-12 mr-auto mb-md-0 mb-6">
                        <input type="text" placeholder="Search by customer..." name="customer" value={filters.customer} onChange={handleFilterChange} className="form-control bg-input border-0" />
                    </div>
                    <div className="col-lg-3 col-md-6 col-12 col-sm-12 mr-auto mb-md-0 mb-6">
                        <select className="form-select" name="sortOrder" value={filters.sortOrder} onChange={handleFilterChange}>
                            <option value="soonest">Dispatch: Soonest first</option>
                            <option value="latest">Dispatch: Latest first</option>
                        </select>
                    </div>
                    <div className="col-lg-3 col-md-6 col-12 col-sm-12 mr-auto mb-md-0 mb-6">
                        <select className="form-select" name="limit" value={filters.limit} onChange={handleFilterChange}>
                            <option value="10">Show 10</option>
                            <option value="20">Show 20</option>
                            <option value="50">Show 50</option>
                            <option value="100">Show 100</option>
                        </select>
                    </div>
                </div>
                <div className="row">
                    <div className="col-lg-3 col-md-6 col-sm-6 col-12">
                        <Link className="mt-4 text-danger" onClick={resetFilters}><i className="fa fa-sync" style={{fontSize:'20px',marginTop:'15px'}}></i></Link>
                    </div>
                </div>
                </div>
                <div className="card-body px-0 pt-7 pb-0">
                    {isLoading ? (
                        <div className="text-center">
                            <Loader />
                        </div>
                    ) : (
                        orders.length > 0 && (
                            <div className="table-responsive">
                                <DataTable orders={orders} extraColumns={scheduledDispatchColumns} renderActions={renderActions} />
                            </div>
                        )
                    )}
                    {!isLoading && isNotFound &&
                        <div className="text-center">
                        <img src="/assets/images/no-data.png" alt="" style={{width:'60px',opacity:'0.5'}} />
                        <h6 style={{color:'#918f8f',fontFamily: 'math',fontWeight: '500'}}>No Data Found</h6>
                        </div>
                    }
                </div>
            </div>
            {totalPages > 1 &&
                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            }
            <DispatchDateModal
                open={Boolean(rescheduleOrder)}
                onCancel={() => setRescheduleOrder(null)}
                onSuccess={handleDispatchDateSaved}
                order={rescheduleOrder}
            />
        </Layout>
    </>
  )
}

export default ScheduledDispatchOrders
